import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OTPVerificationModal = ({ open, handleClose }) => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [timer, setTimer] = useState(120);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [resending, setResending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [srno, setSrno] = useState("");
    const [primaryKeyId, setPrimaryKeyId] = useState(null);
    const [encodeFPTxnId, setEncodeFPTxnId] = useState(null);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
            return () => clearInterval(interval);
        } else {
            setResendDisabled(false);
        }
    }, [timer]);

    useEffect(() => {
        if (open) {
            setTimer(120);
            setOtp(["", "", "", "", "", ""]);
            setResendDisabled(true);
            discoverAvdm(); // Call discoverAvdm first

            // Wait for srno to be set before sending OTP
            const interval = setInterval(() => {
                if (srno) {
                    sendOtp();
                    clearInterval(interval);
                }
            }, 500);

            return () => clearInterval(interval);
        }
    }, [open, srno]);

    const [deviceInfo, setDeviceInfo] = useState("");
    const [isDiscovering, setIsDiscovering] = useState(false);
    const statusLabelRef = useRef(null);
    const ddlAVDMRef = useRef(null);
    const finalUrlRef = useRef("");

    const discoverAvdm = async () => {
        if (isDiscovering) return;
        setIsDiscovering(true);
        let successFlag = 0;
        let primaryUrl = window.location.protocol === "https:" ? "https://127.0.0.1:" : "http://127.0.0.1:";

        if (ddlAVDMRef.current) ddlAVDMRef.current.innerHTML = "";

        for (let i = 11100; i <= 11120; i++) {
            try {
                const response = await fetch(primaryUrl + i.toString(), {
                    method: "RDSERVICE",
                    mode: "cors",
                    headers: { "Content-Type": "text/xml; charset=utf-8" },
                });

                if (!response.ok) continue;

                const data = await response.text();
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(data, "text/xml");

                const status = xmlDoc.querySelector("RDService")?.getAttribute("status");
                const info = xmlDoc.querySelector("RDService")?.getAttribute("info");

                if (info?.includes("Mantra")) {
                    setDeviceInfo(data);
                    finalUrlRef.current = primaryUrl + i;

                    if (ddlAVDMRef.current) {
                        const option = document.createElement("option");
                        option.value = i.toString();
                        option.textContent = `(${status}) ${info}`;
                        ddlAVDMRef.current.appendChild(option);
                    }
                    successFlag = 1;
                    alert("RDSERVICE Discovered Successfully");
                    break;
                }
            } catch (error) {
                console.error("Error discovering AVDM:", error);
            }
        }

        if (successFlag === 0) {
            alert("Connection failed. Please try again.");
        } else {
            await deviceInfoAvdm(); // Fetch device info after successful discovery
        }

        setIsDiscovering(false);
    };

    const deviceInfoAvdm = async () => {
        if (!finalUrlRef.current) {
            alert("Please discover AVDM first.");
            return;
        }

        try {
            const response = await fetch(finalUrlRef.current, {
                method: "DEVICEINFO",
                mode: "cors",
                headers: { "Content-Type": "text/xml; charset=utf-8" },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.text();
            setDeviceInfo(data);
            console.log(data);
            // Parse XML and extract srno
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(data, "text/xml");
            const srno = xmlDoc.querySelector("Param[name='srno']")?.getAttribute("value");

            if (srno) {
                console.log("Extracted SRNO:", srno);
                setSrno(srno); // Store srno in state
            } else {
                console.warn("SRNO not found in the response.");
            }
        } catch (error) {
            console.error("Error fetching device info:", error);
            alert("Failed to fetch device info. Please try again.");
        }
    };

    const handleOtpChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < 5) document.getElementById(`otp-${index + 1}`).focus();
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`).focus();
        }
    };

    const handleResendOtp = async () => {
        setResendDisabled(true);
        setResending(true);
        try {
            setResendDisabled(true); // Disable the button temporarily
            setTimer(30); // Reset the timer

            const token = sessionStorage.getItem("jwtToken"); // Fetch token from localStorage
            if (!token) {
                console.error("Authorization token not found!");
                return;
            }

            const requestBody = {
                primaryKeyId: primaryKeyId,
                encodeFPTxnId: encodeFPTxnId,
                deviceSerialNumber: srno, // Sending srno as part of the payload
            };

            const response = await axios.post(
                "https://api.ipaisa.co.in/myapp/auth/cms/reSendOtp",
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );


            const data = await response.json();

            if (response.ok) {
                console.log("OTP resent successfully!", data);
            } else {
                console.error("Failed to resend OTP:", data.message);
            }
        } catch (error) {
            console.error("Error resending OTP:", error);
        }
        setResending(false);
        setTimer(120);
        setOtp(["", "", "", "", "", ""]);
    }


    const sendOtp = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                toast.error("Geolocation is not supported by your browser.");
                reject("Geolocation not supported");
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const latitude = position.coords.latitude;
                    const longitude = position.coords.longitude;
                    const deviceSerialNumber = srno; // Hardcoded for now

                    try {
                        const response = await axios.post(
                            "https://api.ipaisa.co.in/myapp/auth/cms/sendOtp",
                            {
                                latitude,
                                longitude,
                                deviceSerialNumber,
                            },
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
                                },
                            }
                        );

                        // Show toast based on response structure
                        if (response.data.statusCodeValue === 200) {
                            if (response.data.body && response.data.body.message) {
                                console.log(response.data.statusCodeValue);
                                setPrimaryKeyId(response.data.body.data.primaryKeyId);
                                setEncodeFPTxnId(response.data.body.data.encodeFPTxnId);
                                toast.success(response.data.body.message);
                            } else {
                                toast.success(response.data.message); // Use `response.data.message` as fallback
                            }
                        } else {
                            toast.error(response.data.body.message || "Failed to send OTP.");
                        }

                        resolve(response.data);
                    } catch (error) {
                        console.error("Error sending OTP:", error);
                        toast.error("Failed to send OTP. Please try again.");
                        reject(error);
                    }
                },
                (error) => {
                    console.error("Geolocation error:", error);
                    toast.error("Error getting location. Please allow location access.");
                    reject(error);
                }
            );
        });
    };


    const handleSubmit = async () => {
        console.log("Entered OTP:", otp.join(""));
        const enteredOtp = otp.join(""); // Combine OTP digits into a single string

        if (enteredOtp.length !== 6) {
            toast.error("Please enter a valid 6-digit OTP.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                "https://api.ipaisa.co.in/myapp/auth/cms/validateOtp",
                {
                    otp: enteredOtp,
                    primaryKeyId,
                    encodeFPTxnId,
                    deviceSerialNumber: srno, // Sending srno as part of the payload
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
                    },
                }
            );

            if (response.data.statusCodeValue === 200) {
                console.log(response.data.body.message);
                toast.success(response.data.body.message, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored",
                    onClose: handleClose, // Close modal only after toast disappears
                });
            } else {
                toast.error(response.data.body.message);
            }
        } catch (error) {
            console.error("Error validating OTP:", error);
            toast.error("Failed to verify OTP. Please try again.");
        } finally {
            setLoading(false);
            setOtp(["", "", "", "", "", ""]);
            setTimer(120);
        }
    };



    const handleModalClose = () => {
        setOtp(["", "", "", "", "", ""]);
        setTimer(120);
        setResendDisabled(true);
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleModalClose}>
            <Box
                sx={{
                    width: 400,
                    bgcolor: "white",
                    p: 4,
                    mx: "auto",
                    mt: "15%",
                    borderRadius: 3,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {loading ? <CircularProgress size={20} color="success" /> : null}
                    <Typography variant="body2">OTP sent to 8378054080</Typography>
                </Box>

                <Box display="flex" justifyContent="center" gap={1} my={3}>
                    {otp.map((value, index) => (
                        <TextField
                            key={index}
                            id={`otp-${index}`}
                            value={value}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputProps={{
                                maxLength: 1,
                                style: {
                                    textAlign: "center",
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    borderColor: "#1976d2",
                                    bgcolor: "#F6F6F6",
                                    "&:hover fieldset": { borderColor: "#1565c0" },
                                    "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                                },
                            }}
                        />
                    ))}
                </Box>

                <Typography
                    variant="body2"
                    sx={{
                        bgcolor: timer > 0 ? "#ffeb3b" : "#d32f2f",
                        color: "black",
                        display: "inline-block",
                        px: 2,
                        py: 0.5,
                        borderRadius: "8px",
                        fontWeight: "bold",
                    }}
                >
                    {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")} min left
                </Typography>

                <Button
                    variant="text"
                    onClick={handleResendOtp}
                    disabled={resendDisabled}
                    sx={{ textTransform: "none", mt: 1, color: resendDisabled ? "#9e9e9e" : "#1976d2" }}
                >
                    {resending ? "Resending..." : "Didn't receive OTP? Resend"}
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{
                        mt: 2,
                        py: 1,
                        fontWeight: "bold",
                        borderRadius: 2,
                        bgcolor: otp.every((digit) => digit !== "") ? "#1976d2" : "#9e9e9e",
                    }}
                    onClick={handleSubmit}
                    disabled={otp.some((digit) => digit === "")}
                >
                    {loading ? <CircularProgress size={24} /> : "Verify OTP"}
                </Button>
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    theme="colored"
                />
            </Box>

        </Modal>

    );
};

export default OTPVerificationModal;
